'use client';

import Button from './atoms/Button';
import { formatCurrency } from '../utils/shared';
import EventDTO, { EventPublicStatus } from '../models/event';
import getCustomTicketLabel from '../utils/getCustomTicketLabel';
import getCustomTicketGender from '../utils/getCustomTicketGender';
import { IconTicket2 } from './icons';
import isEventComingSoon from '../utils/isEventComingSoon';
import InstallmentBanks from './atoms/InstallmentBanks';

interface TicketProps {
  price: number | undefined;
  onClick: () => void;
  event: EventDTO;
  // Promos de cuotas con el banco, debajo del precio.
  promotions?: { id: string; bank: string; bank_logo_url?: string | null; installments: number[] }[];
  // Checkout nuevo: el botón muestra que está abriendo la compra (tarda un
  // momento en bajar la página siguiente). Sin esto parecía no hacer nada.
  loading?: boolean;
}

// Sin precio conocido (no quedan entradas no-extra) mostramos solo el título:
// nunca un número inventado ni un "$∞" salido de un Math.min() vacío.
const getPriceLabel = (price: number | undefined) => {
  if (typeof price !== 'number' || !Number.isFinite(price)) {
    return null;
  }
  return price > 0 ? `desde ${formatCurrency(price, 'no-decimals')}` : 'sin cargo';
};

const getCustomSoldOutText = (event: EventDTO) => {
  if (event.custom_ticket_label === 'registro') {
    return 'Registro completo';
  }
  return `${getCustomTicketLabel(event, { plural: true, casing: 'first_letter' })} agotad${getCustomTicketGender(event.custom_ticket_label)}s`;
};

const Ticket: React.FC<TicketProps> = ({
  price, onClick, event, promotions = [], loading = false,
}) => {
  const getPublicStatusText = (eventPublicStatus: EventPublicStatus) => {
    if (event.is_sold_out) {
      return getCustomSoldOutText(event);
    }
    switch (eventPublicStatus) {
      case EventPublicStatus.PendingApproval:
        return 'El evento no ha sido aprobado aún';
      case EventPublicStatus.NoTicketsAvailable:
        return 'No hay entradas disponibles';
      case EventPublicStatus.Expired:
        return 'El evento ha finalizado';
      case EventPublicStatus.Inactive:
        return 'El evento no está activo';
      default:
        return '';
    }
  };

  const getCustomLabelCTA = () => {
    if (event.custom_ticket_label === 'registro') {
      return 'Registrarse';
    }
    return `Comprar ${getCustomTicketLabel(event, { plural: true, casing: 'lower' })}`;
  };

  const priceLabel = getPriceLabel(price);
  const ticketsLabel = getCustomTicketLabel(event, { plural: true, casing: 'first_letter' });
  // Venta todavía no abierta: sin precio ni botón. Los estados de evento no
  // aprobado, inactivo o finalizado tienen prioridad sobre "Próximamente".
  const isComingSoon = isEventComingSoon(event) && (
    event.public_status === EventPublicStatus.Ready
    || event.public_status === EventPublicStatus.NoTicketsAvailable
  );

  if (isComingSoon) {
    return (
      <div className="flex flex-col gap-4 md:gap-6 items-center w-full glass-surface py-4">
        <div className='flex flex-col gap-2 text-text-primary bg-primary-300/20 rounded-3xl p-1 lg:p-4 w-full'>
          <div className='flex flex-row items-center gap-2 text-lg font-semibold justify-center text-center mx-2'>
            {ticketsLabel}
            <IconTicket2 className="w-8 h-8" />
          </div>
          <div className='text-xl font-semibold justify-center text-center mx-2'>
            Próximamente
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6 items-center w-full glass-surface py-4">
      {(event.public_status !== EventPublicStatus.Ready || event.is_sold_out) ? (
        <div className={`text-lg font-semibold justify-center text-center mx-2 ${event.is_sold_out ? 'text-error-600' : 'text-text-primary'}`}>{getPublicStatusText(event.public_status)}</div>
      ) : (
        <>
          <div className='flex flex-col gap-2 text-text-primary bg-primary-300/20 rounded-3xl p-1 lg:p-4 w-full'>
            <div className='flex flex-row items-center gap-2 text-lg font-semibold justify-center text-center mx-2'>
              {getCustomTicketLabel(event, { plural: true, casing: 'first_letter' })}
              <IconTicket2 className="w-8 h-8" />
            </div>
            {priceLabel && (
              <div className='text-xl font-semibold justify-center text-center mx-2'>
                {getCustomTicketLabel(event, { plural: true, casing: 'first_letter' })} {priceLabel}
              </div>
            )}
            <InstallmentBanks promotions={promotions} className="justify-center pt-1" />
          </div>
          <div className="w-full">
            <Button variant="primary" onClick={onClick} loading={loading} disabled={loading}><div className="whitespace-nowrap">{getCustomLabelCTA()}</div></Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Ticket;
